import { useEffect, useMemo, useState } from 'react';
import ApiService from '../../api_services/apiService.jsx';
import './ProfileImagePreview.css';

const normalizeHash = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    if (!text || text.toLowerCase() === 'null' || text === '_PROFILE_') return '';
    return text.startsWith('_PROFILE_') ? text : `_PROFILE_${text}`;
};

const getImageHash = (profile) => normalizeHash(
    profile?.profileImageHashedId
    || profile?.profileImageHash
    || profile?.avatarHash
    || profile?.imageHash
);

const getImageUrl = (profile) => {
    const value = profile?.profileImageUrl || profile?.imageUrl;
    if (!value || String(value).toLowerCase() === 'null') return '';
    return String(value).trim();
};

function ProfileImagePreview({ profile, userId, name, size = 'medium' }) {
    const [loadedProfile, setLoadedProfile] = useState(profile || null);
    const [imageSrc, setImageSrc] = useState('');
    const [imageError, setImageError] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setLoadedProfile(profile || null);
    }, [profile]);

    useEffect(() => {
        if ((getImageHash(loadedProfile) || getImageUrl(loadedProfile)) || !userId) return undefined;

        const controller = new AbortController();
        ApiService.getProfileById(userId, { signal: controller.signal })
            .then((response) => {
                if (!controller.signal.aborted) setLoadedProfile(response?.data ?? response ?? null);
            })
            .catch((error) => {
                if (error?.code !== 'ERR_CANCELED') setLoadedProfile(null);
            });

        return () => controller.abort();
    }, [loadedProfile, userId]);

    const imageHash = useMemo(() => getImageHash(loadedProfile), [loadedProfile]);
    const imageUrl = useMemo(() => getImageUrl(loadedProfile), [loadedProfile]);
    const fallbackLetter = String(
        name
        || loadedProfile?.nick
        || loadedProfile?.name
        || loadedProfile?.email
        || userId
        || '?'
    ).trim().charAt(0).toUpperCase() || '?';

    useEffect(() => {
        let active = true;
        let objectUrl = null;
        const sourceUrl = imageUrl || (imageHash ? `/open/images/${imageHash}` : '');

        setImageSrc('');
        setImageError(false);
        if (!sourceUrl) return undefined;

        ApiService.get(sourceUrl, {
            responseType: 'blob',
            headers: { Accept: 'image/*' },
        })
            .then((response) => {
                if (!active) return;
                objectUrl = URL.createObjectURL(response.data);
                setImageSrc(objectUrl);
            })
            .catch(() => {
                if (active) setImageError(true);
            });

        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [imageHash, imageUrl]);

    useEffect(() => {
        if (!isExpanded) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsExpanded(false);
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isExpanded]);

    return (
        <>
        <div
            className={`profile-image-preview profile-image-preview--${size} ${imageSrc && !imageError ? 'is-clickable' : ''}`}
            onClick={() => imageSrc && !imageError && setIsExpanded(true)}
            onKeyDown={(event) => {
                if ((event.key === 'Enter' || event.key === ' ') && imageSrc && !imageError) {
                    event.preventDefault();
                    setIsExpanded(true);
                }
            }}
            role={imageSrc && !imageError ? 'button' : undefined}
            tabIndex={imageSrc && !imageError ? 0 : undefined}
            aria-label={imageSrc && !imageError ? 'Powiększ zdjęcie profilowe' : undefined}
        >
            {imageSrc && !imageError ? (
                <img src={imageSrc} alt={`Zdjęcie profilowe ${name || 'użytkownika'}`} onError={() => setImageError(true)} />
            ) : (
                <span>{fallbackLetter}</span>
            )}
        </div>
        {isExpanded && imageSrc && !imageError && (
            <div className="profile-image-lightbox" role="presentation" onClick={() => setIsExpanded(false)}>
                <div className="profile-image-lightbox__content" onClick={(event) => event.stopPropagation()}>
                    <button
                        type="button"
                        className="profile-image-lightbox__close"
                        onClick={() => setIsExpanded(false)}
                        aria-label="Zamknij podgląd zdjęcia"
                    >
                        ×
                    </button>
                    <img src={imageSrc} alt={`Powiększone zdjęcie profilowe ${name || 'użytkownika'}`} />
                </div>
            </div>
        )}
        </>
    );
}

export default ProfileImagePreview;
